"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState } from "react"
import { ExternalLink, FlaskConical, Link as LinkIcon, Megaphone, RefreshCw, Unplug } from "lucide-react"

import { IntegrationCardLayout } from "@/components/integrations/integration-card-layout"
import { MetaAdsLiveAdHandoff } from "@/components/integrations/meta-ads/meta-ads-live-ad-handoff"
import { MetaAdsSetupDialog } from "@/components/integrations/meta-ads/meta-ads-setup-dialog"
import { MetaAdsFollowUpDialog } from "@/components/integrations/meta-ads/meta-ads-follow-up-dialog"
import { MetaAdsTestDialog } from "@/components/integrations/meta-ads/meta-ads-test-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  useAcknowledgeMetaAdsFormHandoffMutation,
  useCheckMetaAdsMutation,
  useDisconnectMetaAdsMutation,
} from "@/hooks/mutations/meta-ads"
import { useMetaAdsIntegrationQuery } from "@/hooks/queries/meta-ads"
import {
  canSendMetaAdsCallbackTest,
  getLatestMetaAdsFormHandoffsBySource,
  isMetaAdsFormHandoffInScope,
} from "@/lib/meta-ads/readiness-flow"
import type {
  MetaAdsConnectionHealth,
  MetaAdsConnectionTest,
  MetaFollowUpAction,
} from "@/lib/meta-ads/types"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





export function MetaAdsIntegrationCard({ defaultOpen = false }: { defaultOpen?: boolean }) {


  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsIntegrationCard")
  const tCommonUi = useTranslations("Common")

  const integrationQuery = useMetaAdsIntegrationQuery()
  const checkMutation = useCheckMetaAdsMutation()
  const acknowledgeHandoffMutation = useAcknowledgeMetaAdsFormHandoffMutation()
  const disconnectMutation = useDisconnectMetaAdsMutation()
  const [setupOpen, setSetupOpen] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [followUpOpen, setFollowUpOpen] = useState(false)
  const [handoffError, setHandoffError] = useState<string | null>(null)
  const integration = integrationQuery.data ?? null
  const isConfigured = integration && integration.status !== "disabled"
  const pendingHandoffs = integration
    ? getLatestMetaAdsFormHandoffsBySource(integration.form_handoffs).filter(handoff => (
        !handoff.acknowledgedAt
        && isMetaAdsFormHandoffInScope({
          handoff,
          formScope: integration.form_scope,
          selectedFormIds: integration.selected_form_ids,
        })
      ))
    : []

  async function acknowledgePendingHandoffs() {
    setHandoffError(null)
    try {
      for (const handoff of pendingHandoffs) {
        await acknowledgeHandoffMutation.mutateAsync(handoff.copiedForm.id)
      }
    } catch (error: unknown) {
      setHandoffError(error instanceof Error ? error.message : "The update could not be confirmed.")
    }
  }

  return (
    <>
      <IntegrationCardLayout
        title={tUi("adsPlatforms")}
        description={tUi("scheduleFollowUpCallsEmailsOrWhatsAppMessages")}
        icon={<Megaphone aria-hidden="true" className="h-5 w-5" />}
        defaultOpen={defaultOpen}
      >
        <div className="space-y-4">
          {integrationQuery.isLoading ? (
            <div className="h-24 animate-pulse rounded-lg bg-muted motion-reduce:animate-none" />
          ) : null}
          {integrationQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-3">
                <span><span key={tUi("metaAdsConnectionCouldNotBeLoaded")}>{tUi("metaAdsConnectionCouldNotBeLoaded")}</span></span>
                <Button size="sm" variant="outline" onClick={() => void integrationQuery.refetch()}>
                  <span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {!integrationQuery.isLoading && !integrationQuery.error && !isConfigured ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <span key={tUi("connectMetaAdsThenChooseWhetherAnInstant")}>{tUi("connectMetaAdsThenChooseWhetherAnInstant")}</span></p>
              <Button type="button" onClick={() => setSetupOpen(true)}>
                <LinkIcon aria-hidden="true" className="mr-2 h-4 w-4" />
                <span><span key={tUi("connectAdsPlatform")}>{tUi("connectAdsPlatform")}</span></span>
              </Button>
            </div>
          ) : null}

          {isConfigured ? (
            <div className="space-y-4 rounded-lg border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium"><span key={"Meta Ads"}>{"Meta Ads"}</span></h3>
                    <StatusBadge health={integration.connection_health} />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {integration.meta_page_name} <span><span key={tUi("page")}>{tUi("page")}</span></span> {integration.meta_page_id}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setSetupOpen(true)}>
                    <span key={integration.status === "draft" ? tUi("finishSetup") : tUi("reconnect")}>{integration.status === "draft" ? tUi("finishSetup") : tUi("reconnect")}</span>
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setFollowUpOpen(true)}>
                    <span><span key={tUi("chooseFirstTaskAndFallback")}>{tUi("chooseFirstTaskAndFallback")}</span></span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={integration.connection_health === "verification_pending" || !canSendMetaAdsCallbackTest(integration)}
                    onClick={() => setTestOpen(true)}
                  >
                    <FlaskConical className="mr-2 h-4 w-4" />
                    <span key={integration.connection_test ? tUi("testAgain") : tUi("testConnection")}>{integration.connection_test ? tUi("testAgain") : tUi("testConnection")}</span>
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={checkMutation.isPending} onClick={() => checkMutation.mutate()}>
                    <RefreshCw className="mr-2 h-4 w-4" /> <span><span key={tUi("checkConnection")}>{tUi("checkConnection")}</span></span>
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setDisconnectOpen(true)}>
                    <Unplug className="mr-2 h-4 w-4" /> <span><span key={tUi("disconnect")}>{tUi("disconnect")}</span></span>
                  </Button>
                </div>
              </div>

              <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
                <h4 className="font-medium"><span key={tUi("leadTaskRouting")}>{tUi("leadTaskRouting")}</span></h4>
                {Object.entries(integration.form_action_overrides).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(integration.form_action_overrides).map(([formId, actions]) => (
                      <p key={formId} className="break-words text-muted-foreground">
                        <span><TranslationText>{tUi.rich("formValue0", { value0: String(formId) })}</TranslationText></span>
                        <span key={actions[0] ? actionLabel(actions[0].actionType, tUi) : tUi("noFollowUp")}>{actions[0] ? actionLabel(actions[0].actionType, tUi) : tUi("noFollowUp")}</span>
                        {actions[1] ? <span><TranslationText>{tUi.rich("ifItFailsValue0", { value0: String(actionLabel(actions[1].actionType, tUi)) })}</TranslationText></span> : null}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p key={integration.voice_agent_id
                      ? tUi("formsCurrentlyUseTheLegacyVoiceCallbackChoose")
                      : tUi("noLeadTasksConfiguredYetChooseAFirst")} className="text-muted-foreground">
                    {integration.voice_agent_id
                      ? tUi("formsCurrentlyUseTheLegacyVoiceCallbackChoose")
                      : tUi("noLeadTasksConfiguredYetChooseAFirst")}
                  </p>
                )}
              </div>

              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                {integration.voice_agent_id ? <Detail label={tUi("legacyCallbackAgent")} value={integration.voice_agent_name ?? "Not selected"} /> : null}
                {integration.caller_phone_number ? <Detail label={tUi("legacyCallerNumber")} value={integration.caller_phone_number} /> : null}
                <Detail
                  label={tUi("metaAdAccount")}
                  value={integration.meta_ad_account_name ?? "Choose during reconnect"}
                />
                <Detail
                  label={tUi("leadForms")}
                  value={integration.form_scope === "all"
                    ? "All Instant Forms"
                    : `${integration.selected_form_ids.length} selected`}
                />
                {integration.voice_agent_id ? <Detail label={tUi("legacyCallbackHours")} value={integration.respect_working_hours ? "Keep callbacks inside working hours" : "Call as soon as possible"} /> : null}
                <Detail label={tUi("lastConnectionCheck")} value={formatDateTime(integration.last_checked_at)} />
                <Detail label={tUi("lastLeadEvent")} value={formatDateTime(integration.last_event_at)} />
                <Detail label={tUi("lastLeadTask")} value={formatDateTime(integration.last_task_created_at)} />
              </dl>
              {!canSendMetaAdsCallbackTest(integration) ? <p className="text-sm text-muted-foreground">
                <span key={tUi("toTestEmailOrWhatsAppFollowUpSubmit")}>{tUi("toTestEmailOrWhatsAppFollowUpSubmit")}</span></p> : null}

              {integration.last_error_code ? (
                <Alert variant="destructive">
                  <AlertDescription>{connectionMessage(integration.last_error_code, tUi)}</AlertDescription>
                </Alert>
              ) : null}
              {integration.connection_test ? (
                <ConnectionTestAlert test={integration.connection_test} />
              ) : null}
              {integration.recent_action_results.length > 0 ? (
                <div className="space-y-2 rounded-md border p-3 text-sm">
                  <h4 className="font-medium"><span key={tUi("recentLeadActions")}>{tUi("recentLeadActions")}</span></h4>
                  {integration.recent_action_results.slice(0, 10).map(result => (
                    <p key={`${result.lead_id}:${result.action_stage}`} className="break-words text-muted-foreground">
                      <span><TranslationText>{tUi.rich("value0Value1LeadValue2Value3", { value0: String(result.action_stage), value1: String(result.action_type.replaceAll("_", " ")), value2: String(result.lead_id), value3: String(result.status.replaceAll("_", " ")) })}</TranslationText></span>
                      {result.error_code ? <span> · {followUpErrorMessage(result.error_code, tUi)}</span> : null}
                    </p>
                  ))}
                </div>
              ) : null}
              {pendingHandoffs.length > 0 ? (
                <div className="space-y-3">
                  <MetaAdsLiveAdHandoff
                    handoffs={pendingHandoffs}
                    adAccountName={integration.meta_ad_account_name}
                  />
                  {handoffError ? <p className="text-sm text-destructive">{handoffError}</p> : null}
                  <div className="flex flex-wrap gap-2">
                    <Button asChild type="button" size="sm" variant="outline">
                      <a
                        href={integration.ads_manager_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span><span key={tUi("openAdsManager")}>{tUi("openAdsManager")}</span></span>
                        <ExternalLink aria-hidden="true" className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={acknowledgeHandoffMutation.isPending}
                      onClick={() => void acknowledgePendingHandoffs()}
                    >
                      <span key={acknowledgeHandoffMutation.isPending
                        ? tUi("saving")
                        : tUi("iUpdatedMyAdValue0", { value0: String(pendingHandoffs.length === 1 ? "" : "s") })}>{acknowledgeHandoffMutation.isPending
                        ? tUi("saving")
                        : tUi("iUpdatedMyAdValue0", { value0: String(pendingHandoffs.length === 1 ? "" : "s") })}</span>
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </IntegrationCardLayout>

      <MetaAdsSetupDialog
        open={setupOpen}
        integration={isConfigured ? integration : null}
        onOpenChange={setSetupOpen}
      />
      {isConfigured ? <MetaAdsFollowUpDialog open={followUpOpen} onOpenChange={setFollowUpOpen} integration={integration} /> : null}
      <MetaAdsTestDialog open={testOpen} onOpenChange={setTestOpen} />
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("disconnectAndDeleteMetaAdsData")}>{tUi("disconnectAndDeleteMetaAdsData")}</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span key={tUi("allMetaAdsIntegrationDataStoredInVersafy")}>{tUi("allMetaAdsIntegrationDataStoredInVersafy")}</span></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><span key={tUi("keepConnected")}>{tUi("keepConnected")}</span></AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={disconnectMutation.isPending}
              onClick={event => {
                event.preventDefault()
                disconnectMutation.mutate(undefined, {
                  onSuccess: () => setDisconnectOpen(false),
                })
              }}
            >
              <span key={disconnectMutation.isPending ? tUi("deleting") : tUi("disconnectAndDelete")}>{disconnectMutation.isPending ? tUi("deleting") : tUi("disconnectAndDelete")}</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function actionLabel(actionType: MetaFollowUpAction["actionType"], tUi: ReturnType<typeof useTranslations<"Integrations.IntegrationsMetaAdsMetaAdsIntegrationCard">>): string {
  switch (actionType) {
    case "schedule_call": return tUi("scheduleCall")
    case "send_email": return tUi("sendEmail")
    case "send_whatsapp": return tUi("sendWhatsApp")
  }
}

function followUpErrorMessage(code: string, tUi: ReturnType<typeof useTranslations<"Integrations.IntegrationsMetaAdsMetaAdsIntegrationCard">>): string {
  switch (code) {
    case "form_disclosure_changed": return tUi("reviewTheFormContactDisclosure")
    case "contact_field_missing": return tUi("aMappedFormFieldIsMissing")
    case "lead_email_invalid": return tUi("leadEmailIsInvalid")
    case "lead_phone_invalid": return tUi("leadPhoneNumberIsInvalid")
    case "whatsapp_consent_unavailable": return tUi("whatsAppConsentIsUnavailableOrWithdrawn")
    case "whatsapp_template_unavailable": return tUi("whatsAppTemplateIsUnavailable")
    case "agent_or_sender_unavailable": return tUi("agentOrSendingAccountIsUnavailable")
    case "primary_blocked_before_transport": return tUi("primaryActionStoppedBeforeSending")
    case "voice_no_answer": return tUi("primaryCallWasNotAnswered")
    case "voice_busy": return tUi("primaryCallReachedABusyLine")
    case "voice_voicemail": return tUi("primaryCallReachedVoicemail")
    case "voice_provider_failure": return tUi("primaryCallFailedAtTheProvider")
    default: return code.replaceAll("_", " ")
  }
}

function StatusBadge({ health }: { health: MetaAdsConnectionHealth }) {
  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsIntegrationCard")

  switch (health) {
    case "connected_tested":
      return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><span key={tUi("connectedAndTested")}>{tUi("connectedAndTested")}</span></Badge>
    case "connected_untested":
      return <Badge variant="secondary"><span key={tUi("connectedNotTested")}>{tUi("connectedNotTested")}</span></Badge>
    case "verification_pending":
      return <Badge variant="secondary"><span key={tUi("testInProgress")}>{tUi("testInProgress")}</span></Badge>
    case "action_required_meta":
      return <Badge variant="destructive"><span key={tUi("actionRequiredInMeta")}>{tUi("actionRequiredInMeta")}</span></Badge>
    case "authorization_expired":
      return <Badge variant="destructive"><span key={tUi("authorizationExpired")}>{tUi("authorizationExpired")}</span></Badge>
    case "needs_attention":
      return <Badge variant="destructive"><span key={tUi("needsAttention")}>{tUi("needsAttention")}</span></Badge>
    default: {
      const exhaustiveHealth: never = health
      return exhaustiveHealth
    }
  }
}

function ConnectionTestAlert({ test }: { test: MetaAdsConnectionTest }) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsIntegrationCard")

  switch (test.status) {
    case "submitted":
      return (
        <Alert>
          <AlertDescription><span key={tUi("testLeadSentToMetaWaitingForThe")}>{tUi("testLeadSentToMetaWaitingForThe")}</span></AlertDescription>
        </Alert>
      )
    case "webhook_received":
      return (
        <Alert>
          <AlertDescription><span key={tUi("webhookReceivedRetrievingTheTestLead")}>{tUi("webhookReceivedRetrievingTheTestLead")}</span></AlertDescription>
        </Alert>
      )
    case "lead_retrieved":
      return (
        <Alert>
          <AlertDescription><span key={tUi("testLeadRetrievedCreatingItsCallbackTask")}>{tUi("testLeadRetrievedCreatingItsCallbackTask")}</span></AlertDescription>
        </Alert>
      )
    case "task_created":
      return (
        <Alert>
          <AlertDescription>
            <span><span key={tUi("testPassedTheCallbackTaskWasCreated")}>{tUi("testPassedTheCallbackTaskWasCreated")}</span></span>
            <span key={test.scheduled_for ? tUi("forValue0", { value0: String(new Date(test.scheduled_for).toLocaleString(uiFormattingLocale)) }) : ""}>{test.scheduled_for ? tUi("forValue0", { value0: String(new Date(test.scheduled_for).toLocaleString(uiFormattingLocale)) }) : ""}</span><span>.</span>
          </AlertDescription>
        </Alert>
      )
    case "timed_out":
      return (
        <Alert variant="destructive">
          <AlertDescription>
            <span key={tUi("metaDidNotDeliverTheTestLeadAsk")}>{tUi("metaDidNotDeliverTheTestLeadAsk")}</span></AlertDescription>
        </Alert>
      )
    case "failed":
      return (
        <Alert variant="destructive">
          <AlertDescription>
            <span key={tUi("metaDeliveredTheTestLeadButVersafyCould")}>{tUi("metaDeliveredTheTestLeadButVersafyCould")}</span></AlertDescription>
        </Alert>
      )
    default: {
      const exhaustiveStatus: never = test.status
      return exhaustiveStatus
    }
  }
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

function formatDateTime(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "No events received yet"
}

function connectionMessage(code: string, tUi: ReturnType<typeof useTranslations<"Integrations.IntegrationsMetaAdsMetaAdsIntegrationCard">>): string {
  switch (code) {
    case "leadgen_not_subscribed":
      return tUi("versafyIsNoLongerSubscribedToLeadEvents")
    case "missing_permissions":
      return tUi("theMetaConnectionNoLongerGrantsTheRequired")
    case "connection_check_failed":
      return tUi("theConnectionCouldNotBeCheckedReconnectMeta")
    case "meta_lead_access_required":
      return tUi("aMetaBusinessAdminMustApproveVersafyIn")
    case "meta_lead_access_unverified":
      return tUi("metaLeadRetrievalCouldNotBeVerifiedDue")
    case "form_phone_field_missing":
      return tUi("atLeastOneSelectedInstantFormIsMissing")
    case "no_active_instant_forms":
      return tUi("theConnectedPageHasNoActiveInstantForms")
    case "authorization_expired":
    case "credentials_missing":
      return tUi("theMetaAuthorizationHasExpiredReconnectMetaAds")
    default:
      return tUi("metaAdsNeedsAttentionReconnectTheFacebookPage")
  }
}
